<template>
  <div class="mt-4">
    <div class="row">
      <div class="col-md-3">
        <label for="student-profile-img" class="form-label mb-2">Foto del perfil</label>
        <input type="file" class="form-control mt-2 py-5 px-4" accept="image/*" @change="handleImageUpload">
        <p class="lh-sm mt-2 small text-dark">
          <small>Se recomienda 300px X 300px. Formatos compatibles JPG, JPEG y PNG</small>
        </p>
      </div>
      
      <div class="col-md-9">
        <form @submit.prevent="handleSubmit">
          <div class="form-group mb-3">
            <label for="student-name" class="form-label">Nombre</label>
            <input
              v-model="formData.firstname"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': validation.firstname.error }"
              placeholder="Nombre del Usuario"
              required
            />
            <div v-if="validation.firstname.error" class="invalid-feedback">
              {{ validation.firstname.message }}
            </div>
          </div>

          <div class="form-group mb-3">
            <label for="student-lastname" class="form-label">Apellidos</label>
            <input
              v-model="formData.lastname"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': validation.lastname.error }"
              placeholder="Apellido del usuario"
              required
            />
            <div v-if="validation.lastname.error" class="invalid-feedback">
              {{ validation.lastname.message }}
            </div>
          </div>

          <div class="form-group mb-3">
            <label for="student-position" class="form-label">Cargo</label>
            <input 
              v-model="formData.position" 
              type="text" 
              class="form-control" 
              placeholder="Cargo del usuario"
            />
          </div>

          <div class="form-group mb-3">
            <label for="student-role" class="form-label">Rol dentro de Utel B2B</label>
            <select v-model="formData.role" class="form-control" required>
              <option value="" disabled>Seleccione un rol de usuario</option>
              <option v-for="role in roles" :key="role.id" :value="role.name">
                {{ role.name }}
              </option>
            </select>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="student-mail" class="form-label">Correo electrónico</label>
                <input
                  v-model="formData.email"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': validation.email.error }"
                  placeholder="Email de usuario"
                  @blur="validateEmail"
                  required
                />
                <div v-if="validation.email.error" class="invalid-feedback">
                  {{ validation.email.message }}
                </div>
              </div>
            </div>
            
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="student-phone" class="form-label">Celular</label>
                <input 
                  v-model="formData.cellphone" 
                  type="tel" 
                  class="form-control"
                  :class="{ 'is-invalid': validation.cellphone.error }"
                  placeholder="Número de celular"
                  @input="validatePhone"
                />
                <div v-if="validation.cellphone.error" class="invalid-feedback">
                  {{ validation.cellphone.message }}
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6"></div>
            <div class="col-md-6">
              <div class="d-flex gap-3 justify-content-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary px-4"
                  @click="resetForm"
                  :disabled="isSubmitting"
                >
                  Descartar
                </button>
                <button
                  type="submit"
                  class="btn btn-primary px-4"
                  :disabled="!isFormValid || isSubmitting"
                >
                  {{ isSubmitting ? 'Guardando...' : 'Guardar cambios' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStudentApi } from '../../composables/useStudentApi.js'
import { useValidation } from '../../composables/useValidation.js'
import { useNotifications } from '../../composables/useNotifications.js'

export default {
  name: 'UpdateStudent',
  
  props: {
    get: { type: String, required: true },
    headertab: { type: Array, default: () => [] },
    rol: { type: Array, default: () => [] },
    student: { type: Object, default: () => ({}) }
  },
  
  emits: ['student-updated'],
  
  setup(props, { emit }) {
    const { loading: isSubmitting, updateStudent } = useStudentApi()
    const { validation, validateField, setServerErrors, clearValidation } = useValidation()
    const { notifyStudentUpdated, notifyError } = useNotifications()
    
    const roles = ref(props.rol)
    const selectedImage = ref(null)
    
    const formData = reactive({
      firstname: '',
      lastname: '',
      role: '',
      email: '',
      cellphone: '',
      position: ''
    })
    
    const isFormValid = computed(() => {
      return formData.firstname && 
             formData.lastname && 
             formData.email && 
             formData.role &&
             !validation.firstname.error &&
             !validation.lastname.error &&
             !validation.email.error &&
             !validation.cellphone.error
    })
    
    const initializeForm = () => {
      if (props.student && Object.keys(props.student).length > 0) {
        Object.assign(formData, {
          firstname: props.student.firstname || '',
          lastname: props.student.lastname || '',
          role: props.student.role || '',
          email: props.student.email || '',
          cellphone: props.student.cellphone || '',
          position: props.student.position || ''
        })
      }
    }
    
    const validateEmail = () => {
      validateField('email', formData.email, { required: true })
    }
    
    const validatePhone = () => {
      validateField('cellphone', formData.cellphone)
    }
    
    const handleImageUpload = (event) => {
      selectedImage.value = event.target.files[0]
    }
    
    const handleSubmit = async () => {
      if (!isFormValid.value) return
      
      try {
        const updateData = { ...formData }
        
        // If we have a student ID, update existing student
        if (props.student?.id) {
          await updateStudent(props.get, props.student.id, updateData)
        } else {
          // Otherwise create new student
          await axios.post(props.get, updateData)
        }
        
        await notifyStudentUpdated()
        emit('student-updated', updateData)
        
      } catch (error) {
        if (error.response?.data?.errors) {
          setServerErrors(error.response.data.errors)
        }
        notifyError('Error al guardar los cambios del colaborador')
      }
    }
    
    const resetForm = () => {
      initializeForm()
      clearValidation()
      selectedImage.value = null
    }
    
    onMounted(() => {
      initializeForm()
    })
    
    return {
      formData,
      validation,
      roles,
      isSubmitting,
      isFormValid,
      validateEmail,
      validatePhone,
      handleImageUpload,
      handleSubmit,
      resetForm
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 500;
  color: #333;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>